/**
 * `GET /api/calendar` (Requirements 10, 11, 13).
 *
 * The split of work is design.md's: the server filters, scopes and returns a
 * compact bar payload; the client builds the grid, clips to the year bounds and
 * assigns lanes. Two consequences show up in these schemas:
 *
 * - **Bars carry the true stored dates**, unclipped. Clipping is a rendering
 *   decision (R10.14) and the tooltip and Detail_Panel must show the stored dates,
 *   so a clipped date on the wire would destroy information the client needs.
 * - **`count` is the count of distinct readable Initiative identifiers**, computed
 *   over the same relation the bars come from (R11.9, R13.8). It is not
 *   `bars.length`: a bar list truncated for any reason must not change the count,
 *   and a viewer's count legitimately differs from another's (R3.3).
 *
 * Orientation is part of the query even though the client can re-derive month and
 * calendar-week layouts from cache, because the Sales_Cycle orientation needs the
 * year's cycle definitions and because the export metadata block reports the
 * orientation the view used (R15.3).
 */
import { z } from 'zod';
import { calendarOrientationSchema, initiativeStatusSchema, } from './enums.js';
import { initiativeFilterSchema } from './filters.js';
import { MASTER_DATA_CODE_MAX_LENGTH, MASTER_DATA_NAME_MAX_LENGTH } from './limits.js';
import { idSchema, initiativeRefSchema, planningDateSchema, planningYearSchema, versionSchema, } from './primitives.js';
/** The calendar query: the shared filter grammar plus the orientation of R10.2. */
export const calendarQuerySchema = initiativeFilterSchema.extend({
    orientation: calendarOrientationSchema.default('month'),
});
/**
 * A calendar-relevant milestone marker (R10.10).
 *
 * Only milestone types carrying the calendar-relevance flag reach the client, so
 * the marker layer needs no filtering rule of its own.
 */
export const calendarMilestoneMarkerSchema = z.object({
    milestoneTypeId: idSchema,
    /**
     * The display name of the referenced milestone type, resolved server-side so a
     * marker can name its type rather than showing a generic word (R10.10's "SHALL
     * identify the milestone type of each marker by a text label") - the same reason
     * `initiativeMilestoneSchema.milestoneTypeName` is resolved for the Detail_Panel.
     *
     * `null` only for a payload cached by a build predating this field; the marker
     * then falls back to the generic label. A server response always carries the
     * name: the aggregation joins `milestone_type` to apply the calendar-relevance
     * filter, so the row is guaranteed to exist.
     */
    milestoneTypeName: z.string().min(1).max(MASTER_DATA_NAME_MAX_LENGTH).nullable().default(null),
    plannedDate: planningDateSchema,
    completed: z.boolean(),
});
/**
 * One rendered Initiative (design.md, `CalendarBar`).
 *
 * `version` travels with the bar so the Detail_Panel can detect that the rendered
 * entry is outdated and re-render it from current values (R13.5).
 */
export const calendarBarSchema = z.object({
    id: idSchema,
    initiativeRef: initiativeRefSchema,
    title: z.string(),
    /** Stored, unclipped (R10.14). */
    startDate: planningDateSchema,
    /** Stored, unclipped (R10.14). */
    endDate: planningDateSchema,
    primaryPlatformId: idSchema,
    additionalPlatformIds: z.array(idSchema).default([]),
    status: initiativeStatusSchema,
    priorityId: idSchema,
    /**
     * Whether the referenced priority record carries the Elevated_Priority_Flag
     * (R10.8), resolved server-side so the bar layer needs no priority master data
     * of its own to decide whether to draw the marker - the same reason the legend
     * ships a resolved Platform colour rather than a bare Platform id.
     *
     * Defaulted rather than strictly required so a payload cached by an earlier
     * build still parses (see `freshness.ts`, which re-parses cached entries and
     * silently drops those that fail).
     */
    priorityElevated: z.boolean().default(false),
    /**
     * The display name of the referenced priority record, resolved server-side so
     * the bar can state which priority an Initiative carries rather than only
     * whether it is elevated (R10.6) - the same reason
     * `initiativeMilestoneSchema.milestoneTypeName` is resolved rather than left as
     * an id.
     *
     * `null` only for a payload cached by a build predating this field, which the
     * bar renders by falling back to the elevated/not-elevated wording. A server
     * response always carries the name, which is why the inner schema still
     * requires it to be non-empty.
     */
    priorityName: z.string().min(1).max(MASTER_DATA_NAME_MAX_LENGTH).nullable().default(null),
    isConfidential: z.boolean(),
    /** Every assigned cycle of the rendered year; the Sales_Cycle orientation labels all of them (R7.10). */
    salesCycleIds: z.array(idSchema).default([]),
    milestones: z.array(calendarMilestoneMarkerSchema).default([]),
    changedAfterApproval: z.boolean(),
    version: versionSchema,
});
/**
 * One legend entry: every primary Platform present in the current rendering, with
 * its name and colour (R10.5).
 *
 * `code` is the short text label the bar carries, which is what keeps the encoding
 * readable without colour (R10.6, R18.8).
 */
export const platformLegendEntrySchema = z.object({
    platformId: idSchema,
    code: z.string().min(1).max(MASTER_DATA_CODE_MAX_LENGTH),
    name: z.string().min(1).max(MASTER_DATA_NAME_MAX_LENGTH),
    /** The colour held on the active Platform record; contrast is validated on save (R16.12). */
    colour: z.string().min(1),
});
/**
 * The calendar payload.
 *
 * An empty result is this same shape with `count: 0` and empty lists (R10.16): the
 * client still renders the full-year grid, and nothing in the payload can reveal
 * that a record was withheld (R3.2).
 */
export const calendarResponseSchema = z.object({
    year: planningYearSchema,
    orientation: calendarOrientationSchema,
    /** Distinct readable Initiative identifiers in scope (R11.9, R13.8). */
    count: z.number().int().min(0),
    bars: z.array(calendarBarSchema).default([]),
    legend: z.array(platformLegendEntrySchema).default([]),
});
//# sourceMappingURL=calendar.js.map